/**
 * `RailPanel` — the shell's rail panel: a header (the page's name and a close button) over one page's content. Each page's content component (`GmRoster`, `SquadApp`,
 * `CompendiumArchive`, `PilotPage`, `MechPage`, …) renders here; a cross-page opener (ROSTER opening a pilot,
 * the license's "research ↗", FORGE's SHOW ▸ into PILOT or MECH EDIT) is a rail navigation, so Back returns
 * to the page it came from.
 *
 * The body is a flex column: a page's chrome rows (`.gms-tabs`, the mech bay) keep their height
 * and its scrolling host (`.gms-content` or `.shell-fill`) takes the rest. Pages that lay out
 * from their box (COMPENDIUM's three-pane reflow, CODEX's measured height) read the body's
 * measured `size`.
 */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { resolveActiveMechId, type PilotData } from "@lancer-vtt/domain";
import { Icon } from "../../../lib/Icon";
import { useLancers } from "../../../lib/codex/useLancers";
import { useWorldCodex } from "../../../lib/codex/useWorldCodex";
import { useGameSessionContext } from "../../../lib/game/GameSessionProvider";
import { useSlotNames } from "../../../lib/game/useSlotNames";
import type { UseRoster } from "../../../lib/roster/useRemoteRoster";
import { tipAttrs } from "../../../lib/tooltip";
import { CompendiumArchive } from "../../compendium/CompendiumArchive";
import type { Ref } from "../../compendium/compendiumData";
import { CheatsheetApp, PLAYER_CHEATSHEET_PAGES } from "../CheatsheetApp";
import { CodexApp } from "../codex/CodexApp";
import { DiceApp, type PreselectBonus } from "../DiceApp";
import { GmDirectivePanel } from "../GmDirectivePanel";
import { GmEncounters } from "../GmEncounters";
import { GmRoster } from "../GmRoster";
import { GmSlots } from "../GmSlots";
import { NewEncounterForm } from "../NewEncounterForm";
import { MechPage, PilotPage, mechPageMechId, type SheetPageHost } from "../SheetPages";
import { pilotCallsignLabel } from "../pilotLabel";
import { compendiumPageForRef, useRail, type RailPage } from "../railState";
import { ROLODEX_LIST } from "../RolodexApp";
import { SquadApp } from "../SquadApp";
import { useFlatRoll } from "./railDice";
import { useStage } from "./stageContext";

/** The rail body's measured content box (a ResizeObserver; a sane guess before the first callback). */
function useElementSize(): [React.RefObject<HTMLDivElement | null>, { w: number; h: number }] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setSize({ w: Math.floor(r.width), h: Math.floor(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

export function RailPanel({
  page,
  roster,
  pilotId,
  dicePreselect,
  onOpenDice,
}: {
  page: RailPage;
  roster: UseRoster;
  /** The player's selected pilot (null for the GM and for a pilotless deep-link). */
  pilotId: string | null;
  dicePreselect: PreselectBonus | null;
  onOpenDice: (preselect?: PreselectBonus) => void;
}) {
  const rail = useRail();
  const [bodyRef, size] = useElementSize();
  // The page's `.shell-panel-page` wrapper (Shell) owns the swap animation; this is its content.
  const { title, body } = usePageView({ page, roster, pilotId, size, dicePreselect, onOpenDice });
  return (
    <div className="shell-panel-frame" role="region" aria-label={title}>
      <div className="shell-panel-head">
        <span className="shell-panel-file">{title}</span>
        <span className="shell-panel-fill" />
        <button type="button" className="shell-panel-close" aria-label="Close page" {...tipAttrs({ label: "Close page" })} onClick={() => rail.close()}>
          <Icon icon={faXmark} />
        </button>
      </div>
      <div className="shell-panel-body" ref={bodyRef}>
        {body}
      </div>
    </div>
  );
}

const note = (text: string, action?: ReactNode) => (
  <div className="shell-note">
    {text}
    {action}
  </div>
);

/** One page's header title and content, with its openers bound to rail navigations. */
function usePageView({
  page,
  roster,
  pilotId,
  size,
  dicePreselect,
  onOpenDice,
}: {
  page: RailPage;
  roster: UseRoster;
  pilotId: string | null;
  size: { w: number; h: number };
  dicePreselect: PreselectBonus | null;
  onOpenDice: (preselect?: PreselectBonus) => void;
}): { title: string; body: ReactNode } {
  const session = useGameSessionContext();
  const rail = useRail();
  const slotNameOf = useSlotNames();
  const worldCodex = useWorldCodex();
  const lancers = useLancers(roster);
  const [encListRefresh, setEncListRefresh] = useState(0);
  const isGm = session.role === "gm";
  const actorId = session.actorId!;
  // The owner slot's display name; `null` (an unowned pilot) is "—", never the shared resolver's
  // system sentinel.
  const slotName = (slotId: string | null) => (slotId === null ? "—" : slotNameOf(slotId));
  const openCompendiumAt = (ref: Ref) => rail.open({ app: "compendium", page: compendiumPageForRef(ref) });
  const stage = useStage();
  const rollFlat = useFlatRoll();
  const sheetHost: SheetPageHost = { roster, slotName, onOpenInCompendium: openCompendiumAt, rolls: { load: onOpenDice, roll: rollFlat } };
  const pilots = roster.state?.pilots ?? {};
  // Mint a fresh mech under a pilot (caller-minted ids, undoable) and open it.
  const createMech = (pilot: PilotData) => {
    const mechId = crypto.randomUUID();
    const res = roster.dispatch({ type: "create_mech", actorId, pilotId: pilot.id, mechId, loadoutId: crypto.randomUUID(), name: `Mech ${pilot.mechs.length + 1}` });
    // A new mech has no frame yet, so it opens on its editor.
    if (res.ok) rail.open({ app: "mech", mechId, mode: "edit" });
  };

  switch (page.app) {
    case "pilot": {
      const id = page.pilotId ?? pilotId ?? undefined;
      const pilot = id !== undefined ? pilots[id] : undefined;
      const title = `PILOT — ${pilotCallsignLabel(pilot?.callsign)}`;
      if (id === undefined) {
        return {
          title,
          body: isGm
            ? note("Pick a pilot in ROSTER.", <button type="button" className="gms-tab" onClick={() => rail.open({ app: "roster" })}>OPEN ROSTER</button>)
            : note("No pilot selected. Use SWITCH in the strip to pick one."),
        };
      }
      if (!pilot) return { title, body: <div className="gms-content"><div className="gms-content-message">This pilot is no longer on file.</div></div> };
      return {
        title,
        body: <PilotPage host={sheetHost} pilot={pilot} mode={page.mode} onMode={(mode) => rail.open({ ...page, mode })} onOpenMech={(mechId) => rail.open({ app: "mech", mechId })} />,
      };
    }

    case "mech": {
      // A player's MECH is their selected pilot's; the GM's is whichever pilot owns the mech.
      const pilot = isGm
        ? Object.values(pilots).find((p) => p.mechs.some((m) => m.id === page.mechId))
        : pilotId !== null
          ? pilots[pilotId]
          : undefined;
      const mechId = pilot ? mechPageMechId(pilot, page.mechId) : page.mechId;
      const mech = mechId !== undefined ? pilot?.mechs.find((m) => m.id === mechId) : undefined;
      const title = `MECH — ${(page.mode === "hangar" || mechId === undefined ? "HANGAR" : mech?.name || "MECH").toUpperCase()}`;
      if (!pilot) {
        return { title, body: isGm ? note("Pick a mech in ROSTER.", <button type="button" className="gms-tab" onClick={() => rail.open({ app: "roster" })}>OPEN ROSTER</button>) : note("No pilot selected. Use SWITCH in the strip to pick one.") };
      }
      return {
        title,
        body: (
          <MechPage
            host={sheetHost}
            pilot={pilot}
            mechId={page.mechId}
            mode={page.mode ?? "view"}
            onOpenMech={(id, mode) => rail.open({ app: "mech", ...(id !== undefined ? { mechId: id } : {}), ...(mode === "edit" || mode === "hangar" ? { mode } : {}) })}
            onCreateMech={() => createMech(pilot)}
          />
        ),
      };
    }

    case "squad":
      return {
        title: "SQUAD — PILOTS AND MECHS · READ-ONLY",
        body: (
          <div className="gms-content unit-sheet-host">
            <SquadApp
              roster={roster}
              playerSlotIds={new Set(session.slots.filter((s) => s.role === "player").map((s) => s.id))}
              viewedPilotId={page.pilotId}
              slotName={slotName}
              onSelect={(viewed) => rail.open(viewed === undefined ? { app: "squad" } : { app: "squad", pilotId: viewed })}
              onOpenInCompendium={openCompendiumAt}
            />
          </div>
        ),
      };

    case "compendium":
      return {
        title: "COMPENDIUM — OMNINET ARCHIVE",
        body: (
          <div className="shell-fill">
            <CompendiumArchive size={size} page={page.page} onPage={(next) => rail.open({ app: "compendium", page: next })} />
          </div>
        ),
      };

    case "codex": {
      // The open file rides the path.
      const openRolodex = (entryId: string) => rail.open(entryId === ROLODEX_LIST ? { app: "codex", tab: "rolodex" } : { app: "codex", tab: "rolodex", id: entryId });
      return {
        title: "CODEX — CONTACT REGISTRY",
        body: (
          <CodexApp
            content={worldCodex}
            lancers={lancers}
            viewer={isGm ? "gm" : "player"}
            actions={{
              // The GM has no SQUAD page, so a lancer opens in PILOT (`decisions/0205` point 6).
              onOpenSquad: (id) => rail.open(isGm ? { app: "pilot", pilotId: id, mode: "view" } : { app: "squad", pilotId: id }),
              onEditInPilot: (id) => rail.open({ app: "pilot", pilotId: id, mode: "edit" }),
            }}
            backdrop={isGm ? { pinned: session.backdrop, onPin: session.setBackdrop } : undefined}
            viewedEntryId={page.id ?? ROLODEX_LIST}
            onSelectEntry={openRolodex}
            onBack={() => openRolodex(ROLODEX_LIST)}
            maxHeight={size.h}
          />
        ),
      };
    }

    case "cheatsheet": {
      const pages = isGm ? undefined : PLAYER_CHEATSHEET_PAGES;
      const pageId = page.pageId ?? (isGm ? "skill-checks" : "setting");
      return {
        title: isGm ? "CHEATSHEET — RULES REFERENCE" : "CHEATSHEET — SESSION ZERO",
        body: (
          <div className="shell-fill">
            <CheatsheetApp page={{ pageId }} onPage={(next) => rail.open({ app: "cheatsheet", pageId: next.pageId })} pages={pages} />
          </div>
        ),
      };
    }

    case "dice": {
      // A player's page only (the GM rolls from COMMS): titled with the pilot it rolls for.
      const pilot = pilotId !== null ? pilots[pilotId] : undefined;
      const mechId = pilot ? resolveActiveMechId(pilot) : undefined;
      const mech = pilot?.mechs.find((m) => m.id === mechId);
      const who = pilot ? [pilotCallsignLabel(pilot.callsign), ...(mech ? [mech.name.toUpperCase()] : []), `LL${pilot.level}`].join(" · ") : "NO PILOT";
      return {
        title: `DICE — ${who}`,
        body: (
          <div className="gms-content">
            <DiceApp roster={roster} preselect={dicePreselect} />
          </div>
        ),
      };
    }

    case "encounters":
      if (page.view === "new") {
        return {
          title: "ENCOUNTERS — NEW ENCOUNTER",
          body: (
            <div className="gms-content">
              <NewEncounterForm
                hostKey={session.hostKey}
                onCreated={(id) => {
                  // Back to the list, with the new encounter on this GM's stage (a preview).
                  setEncListRefresh((n) => n + 1);
                  stage?.peek(id);
                  rail.open({ app: "encounters", view: "list" });
                }}
                onCancel={() => rail.open({ app: "encounters", view: "list" })}
              />
            </div>
          ),
        };
      }
      return {
        title: "ENCOUNTERS — SCENES",
        body: (
          <div className="gms-content">
            <GmEncounters
              hostKey={session.hostKey}
              refreshKey={encListRefresh}
              onNew={() => rail.open({ app: "encounters", view: "new" })}
              roster={roster.state}
            />
          </div>
        ),
      };

    case "roster":
      return {
        title: "ROSTER — ALL PILOTS",
        body: (
          <div className="gms-content">
            <GmRoster
              roster={roster}
              slotName={slotName}
              actorId={actorId}
              playerSlotIds={new Set(session.slots.filter((s) => s.role === "player").map((s) => s.id))}
              activeEncounterId={session.activeEncounterId}
              onSquadRepaired={(sub) => session.sendFx({ type: "full-repair", payload: { sub } })}
              openPilotId={null}
              openMechId={null}
              onOpenPilot={(id) => rail.open({ app: "pilot", pilotId: id, mode: "view" })}
              onOpenMech={(_pilotId, mechId) => rail.open({ app: "mech", mechId })}
              onCreateMech={(id) => {
                const pilot = pilots[id];
                if (pilot) createMech(pilot);
              }}
            />
          </div>
        ),
      };

    case "settings":
      return { title: "SETTINGS — WHO SITS AT THE TABLE", body: <div className="gms-content"><GmSlots pilots={pilots} /></div> };

    case "director":
      return { title: "DIRECTOR — PLAYER SCREENS", body: <div className="gms-content"><GmDirectivePanel roster={roster} /></div> };
  }
}
